import React from 'react';
import { MerchantInvoice, MerchantBillingSettings } from '../../../types/gst';

interface Props {
  invoice: MerchantInvoice;
  settings?: MerchantBillingSettings;
}

export const ThermalPosTemplate: React.FC<Props> = ({ invoice, settings }) => {
  const sellerName = invoice.sellerTradeName || invoice.sellerLegalName || settings?.companyName || 'Retail Mart';
  const sellerGstin = invoice.sellerGstin || settings?.gstin || '';
  const sellerAddress = invoice.sellerAddress || settings?.address || '';
  const sellerPhone = invoice.sellerPhone || settings?.phone || '';

  return (
    <div className="bg-white text-black p-4 font-mono max-w-xs mx-auto border border-dashed border-slate-400 shadow-sm text-[11px] leading-tight print:p-0 print:border-none print:shadow-none print:max-w-none">
      {/* Receipt Header */}
      <div className="text-center pb-2 border-b border-dashed border-black">
        <h2 className="text-sm font-black uppercase tracking-tight">{sellerName}</h2>
        <div className="text-[10px] text-slate-600 mt-0.5">{sellerAddress}</div>
        {sellerPhone && <div className="text-[10px]">Tel: {sellerPhone}</div>}
        {sellerGstin && <div className="text-[10px] font-bold">GSTIN: {sellerGstin}</div>}
        <div className="font-bold uppercase tracking-wider text-[11px] mt-1">*** RETAIL TAX INVOICE ***</div>
      </div>

      {/* Invoice Meta */}
      <div className="py-2 border-b border-dashed border-black text-[10px] space-y-0.5">
        <div>Inv #: <strong>{invoice.invoiceNumber}</strong></div>
        <div>Date : {invoice.invoiceDate}</div>
        <div>Cust : {invoice.customerName}</div>
        {invoice.customerGstin && <div>Cust GSTIN: {invoice.customerGstin}</div>}
        <div>POS  : {invoice.placeOfSupply}</div>
      </div>

      {/* Itemized List */}
      <div className="py-2 border-b border-dashed border-black">
        <div className="flex justify-between font-bold text-[10px] pb-1 border-b border-slate-300">
          <span>ITEM</span>
          <span>QTY x RATE</span>
          <span>TOTAL</span>
        </div>
        <div className="space-y-1.5 pt-1">
          {invoice.lineItems.map((item, idx) => (
            <div key={idx}>
              <div className="font-bold truncate">{item.description}</div>
              <div className="flex justify-between text-[10px] text-slate-700">
                <span>HSN: {item.hsnSac || '—'}</span>
                <span>{item.quantity} x ₹{item.unitPrice} ({item.gstRate}%)</span>
                <span className="font-bold text-black">₹{item.totalAmount.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Totals */}
      <div className="py-2 border-b border-dashed border-black space-y-1 text-[11px]">
        <div className="flex justify-between">
          <span>Taxable:</span>
          <span>₹{invoice.subtotalTaxable.toFixed(2)}</span>
        </div>
        {invoice.isInterState ? (
          <div className="flex justify-between">
            <span>IGST:</span>
            <span>₹{invoice.totalIgst.toFixed(2)}</span>
          </div>
        ) : (
          <>
            <div className="flex justify-between">
              <span>CGST:</span>
              <span>₹{invoice.totalCgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>SGST:</span>
              <span>₹{invoice.totalSgst.toFixed(2)}</span>
            </div>
          </>
        )}
        {invoice.roundOff !== 0 && (
          <div className="flex justify-between text-[10px]">
            <span>Round Off:</span>
            <span>₹{invoice.roundOff.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-black text-sm pt-1 border-t border-black">
          <span>NET TOTAL:</span>
          <span>₹{invoice.grandTotal.toFixed(2)}</span>
        </div>
        {invoice.balanceDue > 0 && (
          <div className="flex justify-between text-rose-700 font-bold">
            <span>BAL DUE:</span>
            <span>₹{invoice.balanceDue.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Footer / QR info */}
      <div className="text-center pt-2 text-[9px] text-slate-600 space-y-1">
        {settings?.upiId && <div>Pay UPI: <strong>{settings.upiId}</strong></div>}
        <div>Thank you! Visit again.</div>
        <div className="text-[8px] text-slate-400">Powered by Verve AI Labs &bull; Statutory GST Billing</div>
      </div>
    </div>
  );
};
